import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, X, Check, AlertTriangle, RotateCw } from "lucide-react";

/**
 * Modal con escáner de código de barras usando la cámara del dispositivo.
 *
 * Props:
 *   open       — bool
 *   onClose    — fn() para cerrar
 *   onDetected — fn(barcode: string) al detectar un código (cierra modal automáticamente)
 */
export default function BarcodeScannerModal({ open, onClose, onDetected }) {
  const scannerRef = useRef(null);
  const containerId = "barcode-scanner-container";
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  const stoppedRef = useRef(true);

  // Detectar cámaras disponibles cuando se abre
  useEffect(() => {
    if (!open) return;
    setError(null); setLastCode(null);
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!devices || devices.length === 0) {
          setError("No se detectó ninguna cámara en este dispositivo.");
          return;
        }
        setCameras(devices);
        // Preferir cámara trasera en móviles
        const back = devices.find((d) => /back|rear|environment/i.test(d.label));
        setCameraId((back || devices[devices.length - 1]).id);
      })
      .catch((err) => {
        setError("No se pudo acceder a la cámara: " + (err?.message || err) +
                 ". Asegúrate de permitir el acceso en el navegador.");
      });
    return () => {};
  }, [open]);

  // Arrancar / detener escáner cuando cambia cameraId
  useEffect(() => {
    if (!open || !cameraId) return;

    let mounted = true;
    setStarting(true);
    setError(null);

    const start = async () => {
      try {
        // Esperar que el div del container esté en DOM
        await new Promise((r) => setTimeout(r, 100));
        if (!mounted) return;
        const el = document.getElementById(containerId);
        if (!el) {
          setError("Contenedor de escáner no disponible.");
          return;
        }
        scannerRef.current = new Html5Qrcode(containerId, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
          verbose: false,
        });
        stoppedRef.current = false;
        await scannerRef.current.start(
          cameraId,
          {
            fps: 15,
            qrbox: (vw, vh) => {
              const w = Math.min(vw, vh) * 0.8;
              return { width: Math.floor(w), height: Math.floor(w * 0.55) };
            },
            aspectRatio: 1.6,
          },
          (decoded) => {
            if (!mounted) return;
            setLastCode(decoded);
            // Vibración corta
            try { navigator.vibrate?.(60); } catch (_) {}
            // Cerrar y emitir
            stop().then(() => onDetected?.(decoded));
          },
          () => { /* errores de frames se ignoran */ }
        );
        if (mounted) setStarting(false);
      } catch (err) {
        setError("Error iniciando cámara: " + (err?.message || err));
        setStarting(false);
      }
    };

    const stop = async () => {
      if (stoppedRef.current) return;
      stoppedRef.current = true;
      try {
        if (scannerRef.current) {
          await scannerRef.current.stop().catch(() => {});
          await scannerRef.current.clear().catch(() => {});
        }
      } catch (_) { /* noop */ }
      scannerRef.current = null;
    };

    start();
    return () => {
      mounted = false;
      stop();
    };
  }, [open, cameraId, onDetected]);

  const handleClose = () => {
    if (scannerRef.current && !stoppedRef.current) {
      scannerRef.current.stop().catch(() => {});
      stoppedRef.current = true;
    }
    onClose?.();
  };

  const switchCamera = () => {
    if (cameras.length < 2) return;
    const i = cameras.findIndex((c) => c.id === cameraId);
    const next = cameras[(i + 1) % cameras.length];
    setCameraId(next.id);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md" data-testid="barcode-scanner-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" /> Escanear código de barras
          </DialogTitle>
          <DialogDescription className="text-xs">
            Apunta la cámara del dispositivo hacia el código de barras del producto. Soporta EAN, UPC, Code 128 y QR.
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 p-4 text-sm flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-destructive mb-1">No se pudo activar la cámara</div>
              <div className="text-muted-foreground">{error}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="relative aspect-[16/10] bg-black rounded-md overflow-hidden">
              <div id={containerId} className="w-full h-full" />
              {starting && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40 z-10">
                  Iniciando cámara…
                </div>
              )}
              {lastCode && (
                <div className="absolute inset-0 flex items-center justify-center bg-success/40 z-10 animate-in fade-in">
                  <div className="bg-card border-2 border-success px-4 py-2 rounded-md font-mono text-base font-bold flex items-center gap-2">
                    <Check className="w-5 h-5 text-success" /> {lastCode}
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground text-center mt-2">
              Apunta la cámara al código de barras. Funciona con EAN-13, UPC, Code 128, QR y más.
            </div>
          </>
        )}

        <DialogFooter className="flex-row sm:justify-between">
          {cameras.length > 1 ? (
            <Button variant="outline" size="sm" onClick={switchCamera} data-testid="scanner-switch-cam">
              <RotateCw className="w-4 h-4 mr-1.5" /> Cambiar cámara
            </Button>
          ) : <div />}
          <Button variant="outline" onClick={handleClose} data-testid="scanner-close">
            <X className="w-4 h-4 mr-1.5" /> Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
